import React, { useState, useEffect } from 'react';
import {
  Paper,
  Grid,
  Typography,
  Box,
  alpha,
  useTheme,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import relatoriosApi from '../../services/relatoriosApi';
import RelatorioAtendentes from './RelatorioAtendentes';
import AssessmentIcon from '@mui/icons-material/Assessment';

const RelatorioGeral = ({ dados, customTheme, carregando, filtros }) => {
  const theme = useTheme();
  const [dadosGrafico, setDadosGrafico] = useState(null);
  const [carregandoDados, setCarregandoDados] = useState(false);
  const [erro, setErro] = useState(null);
  const [metricas, setMetricas] = useState(null);
  const [carregandoMetricas, setCarregandoMetricas] = useState(false);
  const [servicosPorTempo, setServicosPorTempo] = useState([]);
  const [carregandoServicos, setCarregandoServicos] = useState(false);
  const [diasMovimentados, setDiasMovimentados] = useState([]);
  const [carregandoDias, setCarregandoDias] = useState(false);

  // Cores para os gráficos
  const cores = [
    customTheme.palette.primary.main,
    '#4caf50',
    '#ff9800',
    '#f44336',
    '#9c27b0',
    '#2196f3'
  ];

  // Buscar dados específicos para gráficos
  useEffect(() => {
    if (filtros) {
      buscarDadosGraficos();
      buscarMetricas();
      buscarServicosPorTempo();
      buscarDiasMovimentados();
    }
  }, [filtros]);

  // Buscar dados para gráficos
  const buscarDadosGraficos = async () => {
    try {
      setCarregandoDados(true);
      setErro(null);
      
      const filtrosApi = {
        dataInicio: filtros.dataInicio?.format('YYYY-MM-DD'),
        dataFim: filtros.dataFim?.format('YYYY-MM-DD')
      };
      
      const response = await relatoriosApi.buscarMovimentoPorHorario(filtrosApi);
      
      if (response.success) {
        setDadosGrafico(response.data);
      } else {
        console.error('[DEBUG] RelatorioGeral - Erro na resposta:', response.message);
        setErro(response.message || 'Erro ao buscar dados dos gráficos');
        setDadosGrafico(null);
      }
    } catch (error) {
      console.error('[DEBUG] RelatorioGeral - Erro ao buscar dados:', error);
      setErro('Erro ao carregar dados dos gráficos do servidor');
      setDadosGrafico(null);
    } finally {
      setCarregandoDados(false);
    }
  };

  // Buscar métricas para o resumo executivo
  const buscarMetricas = async () => {
    try {
      setCarregandoMetricas(true);
      setErro(null);
      
      const filtrosApi = {
        dataInicio: filtros.dataInicio?.format('YYYY-MM-DD'),
        dataFim: filtros.dataFim?.format('YYYY-MM-DD')
      };
      
      // Buscar estatísticas diretamente da API
      const estatisticasResponse = await relatoriosApi.buscarEstatisticasApi(filtrosApi);
      
      if (estatisticasResponse.success) {
        // Apenas métricas para o componente
        setMetricas({
          senhasEmitidas: estatisticasResponse.data.senhasEmitidas || 0,
          senhasFinalizadas: estatisticasResponse.data.senhasFinalizadas || 0,
          senhasCanceladas: estatisticasResponse.data.senhasCanceladas || 0,
          tempoMedioAtendimento: estatisticasResponse.data.tempoMedioAtendimento || '0 min',
          tempoMedioEspera: estatisticasResponse.data.tempoMedioEspera || '0 min',
          tempoMedioAtendimentoPreferenciais: estatisticasResponse.data.tempoMedioAtendimentoPreferenciais || '0 min',
          tempoMedioAtendimentoComuns: estatisticasResponse.data.tempoMedioAtendimentoComuns || '0 min',
          tempoMedioEsperaPreferenciais: estatisticasResponse.data.tempoMedioEsperaPreferenciais || '0 min',
          tempoMedioEsperaComuns: estatisticasResponse.data.tempoMedioEsperaComuns || '0 min'
        });
        
        // Dados completos para uso geral
        if (dados) {
          dados.atendimentosPreferenciais = estatisticasResponse.data.atendimentosPreferenciais || 0;
          dados.tempoMedioEspera = estatisticasResponse.data.tempoMedioEspera || '0 min';
          dados.distribuicaoPorServico = estatisticasResponse.data.distribuicaoPorServico || [];
        }
      } else {
        // Se falhar, definir métricas padrão
        setMetricas({
          senhasEmitidas: 0,
          senhasFinalizadas: 0,
          senhasCanceladas: 0,
          tempoMedioAtendimento: '0 min',
          tempoMedioEspera: '0 min',
          tempoMedioAtendimentoPreferenciais: '0 min',
          tempoMedioAtendimentoComuns: '0 min',
          tempoMedioEsperaPreferenciais: '0 min',
          tempoMedioEsperaComuns: '0 min'
        });
      }
    } catch (error) {
      console.error('Erro ao buscar métricas:', error);
      // Definir métricas padrão em caso de erro
      setMetricas({
        senhasEmitidas: 0,
        senhasFinalizadas: 0,
        senhasCanceladas: 0,
        tempoMedioAtendimento: '0 min',
        tempoMedioEspera: '0 min',
        tempoMedioAtendimentoPreferenciais: '0 min',
        tempoMedioAtendimentoComuns: '0 min',
        tempoMedioEsperaPreferenciais: '0 min',
        tempoMedioEsperaComuns: '0 min'
      });
    } finally {
      setCarregandoMetricas(false);
    }
  };

  // Buscar serviços ordenados por tempo médio de atendimento
  const buscarServicosPorTempo = async () => {
    try {
      setCarregandoServicos(true);
      
      const filtrosApi = {
        dataInicio: filtros.dataInicio?.format('YYYY-MM-DD'),
        dataFim: filtros.dataFim?.format('YYYY-MM-DD')
      };
      
      const response = await relatoriosApi.buscarServicosPorTempo(filtrosApi);
      
      if (response.success) {
        setServicosPorTempo(response.data);
      }
    } catch (error) {
      // Falha silenciosa
    } finally {
      setCarregandoServicos(false);
    }
  };

  // Buscar dias mais movimentados
  const buscarDiasMovimentados = async () => {
    try {
      setCarregandoDias(true);
      
      const filtrosApi = {
        dataInicio: filtros.dataInicio?.format('YYYY-MM-DD'),
        dataFim: filtros.dataFim?.format('YYYY-MM-DD')
      };
      
      const response = await relatoriosApi.buscarDatasMovimentadas(filtrosApi);
      
      if (response.success && response.data.diasMaisMovimentados) {
        setDiasMovimentados(response.data.diasMaisMovimentados);
      } else {
        setDiasMovimentados([]);
      }
    } catch (error) {
      // Falha silenciosa
      setDiasMovimentados([]);
    } finally {
      setCarregandoDias(false);
    }
  };



  // Preparar dados para o gráfico de distribuição por serviços
  const dadosDistribuicao = dados?.distribuicaoPorServico?.map((item, index) => ({
    nome: item.servico,
    valor: item.quantidade,
    cor: cores[index % cores.length]
  })) || [];
  
  // Calcular o total para porcentagens
  const totalServicos = dadosDistribuicao.reduce((sum, item) => sum + item.valor, 0);
  
  // Adicionar porcentagem aos dados
  const dadosDistribuicaoComPorcentagem = dadosDistribuicao.map(item => ({
    ...item,
    porcentagem: totalServicos > 0 ? Math.round((item.valor / totalServicos) * 100) : 0
  }));

  // Dados para gráfico de movimento por horário
  const dadosMovimento = dadosGrafico?.movimentoPorHorario || [];

  // Componente customizado para o rótulo do gráfico de pizza
  const renderCustomizedLabel = (props) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, nome, porcentagem } = props;
    const RADIAN = Math.PI / 180;
    const radius = outerRadius * 1.1;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    // Quebrar o texto se for muito longo
    const maxLength = 14;
    let displayName = nome;
    if (nome && nome.length > maxLength) {
      displayName = `${nome.substring(0, maxLength)}...`;
    }
    
    return (
      <text 
        x={x} 
        y={y} 
        fill={theme.palette.text.primary}
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12px"
        fontWeight="500"
      >
        {`${displayName}: ${porcentagem}%`}
      </text>
    );
  };

  // Renderizar legenda customizada
  const renderLegendItem = (props) => {
    const { payload } = props;
    
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        maxHeight: 280, 
        overflowY: 'auto',
        mt: 0,
        p: 0.5,
        borderRadius: 1,
        bgcolor: alpha(theme.palette.background.paper, 0.6),
        width: '100%',
        fontSize: '0.7rem'
      }}>
        {payload.map((entry, index) => (
          <Box
            key={`legend-item-${index}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 0.3,
              fontSize: '0.7rem',
              minHeight: '16px'
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: 1,
                backgroundColor: entry.color,
                mr: 0.5,
                flexShrink: 0
              }}
            />
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              width: '100%',
              alignItems: 'center',
              fontSize: '0.65rem'
            }}>
              <span 
                title={entry.payload.nome}
                style={{ 
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '70%',
                  fontSize: '0.9rem'
                }}
              >
                {entry.payload.nome}
              </span>
              <span style={{ 
                marginLeft: 4, 
                fontSize: '0.9rem',
                fontWeight: 600,
                color: entry.color
              }}>
                {entry.payload.porcentagem}%
              </span>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // Tooltip customizado
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            p: 2,
            border: `1px solid ${alpha(customTheme.palette.primary.main, 0.3)}`,
            borderRadius: 2,
            boxShadow: theme.shadows[4]
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {`${payload[0]?.payload?.nome || label}`}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color }}>
              {entry.name === 'valor' 
                ? `Quantidade: ${entry.value} (${payload[0]?.payload?.porcentagem || 0}%)`
                : `${entry.name}: ${entry.value}`}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  if (carregando) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (erro) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {erro}
      </Alert>
    );
  }

  // Calcular taxa de conclusão com base nas métricas
  const taxaConclusao = metricas && metricas.senhasEmitidas > 0
    ? ((metricas.senhasFinalizadas / metricas.senhasEmitidas) * 100).toFixed(1)
    : 0;

  // Verificar se tem senhas aguardando atendimento
  const senhasAguardando = dados?.senhasAguardando || 0;

  // Obter dados de tempo médio de espera (se disponível)
  const tempoMedioEspera = dados?.tempoMedioEspera || '0 min';

  // Contar chamadas ativas
  const chamadasAtivas = dados?.senhasChamando || 0;

  return (
    <Grid container spacing={3}>
      {/* Gráfico de Movimento por Horário */}
      <Grid item xs={12} lg={8}>
        <Card sx={{ height: '450px', borderRadius: 3 }}>
          <CardContent sx={{ height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Movimento por Horário
            </Typography>
            {carregandoDados ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '350px' }}>
                <CircularProgress />
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={dadosMovimento}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis 
                    dataKey="hora" 
                    stroke={theme.palette.text.secondary}
                    fontSize={12}
                  />
                  <YAxis 
                    stroke={theme.palette.text.secondary}
                    fontSize={12}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="quantidade" 
                    stroke={customTheme.palette.primary.main}
                    strokeWidth={3}
                    dot={{ fill: customTheme.palette.primary.main, strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: customTheme.palette.primary.main, strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Distribuição por Serviços */}
      <Grid item xs={12} lg={4}>
        <Card sx={{ height: '450px', borderRadius: 3 }}>
          <CardContent sx={{ height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Distribuição por Serviços
            </Typography>
            {dadosDistribuicaoComPorcentagem.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={dadosDistribuicaoComPorcentagem}
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={40}
                    paddingAngle={2}
                    dataKey="valor"
                    nameKey="nome"
                    labelLine={false}
                    label={false}
                  >
                    {dadosDistribuicaoComPorcentagem.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.cor} 
                        stroke={theme.palette.background.paper}
                        strokeWidth={1}
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    content={<CustomTooltip />}
                    wrapperStyle={{ zIndex: 100 }}
                  />
                  <Legend 
                    content={renderLegendItem}
                    layout="vertical"
                    verticalAlign="middle" 
                    align="right"
                    wrapperStyle={{
                      paddingLeft: '10px',
                      width: '45%',
                      height: '75%',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '350px' }}>
                <Typography variant="body2" color="text.secondary">
                  Nenhum dado disponível para exibir
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Relatório de Atendentes */}
      <Grid item xs={12}>
        <RelatorioAtendentes 
          dados={dados} 
          customTheme={customTheme}
          carregando={carregandoDados}
          filtros={filtros}
        />
      </Grid>

      {/* Resumo Executivo */}
      <Grid item xs={12}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              <AssessmentIcon sx={{ mr: 1, color: customTheme.palette.primary.main }} />
              Relatório Gerencial
            </Typography>
            
            {carregandoMetricas ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={30} />
              </Box>
            ) : (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: customTheme.palette.primary.main }}>
                  Análise Consolidada do Período
                </Typography>
                
                <Typography variant="body1" paragraph>
                  No período analisado, o sistema de gerenciamento de senhas registrou um total de <strong>{metricas?.senhasEmitidas || 0}</strong> senhas emitidas, 
                  das quais <strong>{metricas?.senhasFinalizadas || 0}</strong> foram concluídas com sucesso, 
                  resultando em uma taxa de conclusão de <strong>{taxaConclusao}%</strong>.
                  O tempo médio de atendimento foi de <strong>{metricas?.tempoMedioAtendimento || '0 min'}</strong>, 
                  enquanto o tempo médio de espera foi de <strong>{tempoMedioEspera}</strong>.
                </Typography>
                
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2.5, bgcolor: alpha(customTheme.palette.primary.main, 0.05), borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: customTheme.palette.primary.main }}>
                        Indicadores de Desempenho
                      </Typography>
                      
                      <Grid container spacing={2} sx={{ mb: 2.5 }}>
                        <Grid item xs={6}>
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Taxa de Conclusão
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {taxaConclusao}%
                            </Typography>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Tempo Médio de Atendimento
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {metricas?.tempoMedioAtendimento || '0 min'}
                            </Typography>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Tempo Médio de Espera
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {tempoMedioEspera}
                            </Typography>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Senhas Canceladas
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {metricas?.senhasCanceladas || 0}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            Métricas por Tipo de Senha
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Tempo Médio Atendimento Preferencial
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {metricas?.tempoMedioAtendimentoPreferenciais || '0 min'}
                            </Typography>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Tempo Médio Atendimento Comum
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {metricas?.tempoMedioAtendimentoComuns || '0 min'}
                            </Typography>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Tempo Médio Espera Preferencial
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {metricas?.tempoMedioEsperaPreferenciais || '0 min'}
                            </Typography>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Tempo Médio Espera Comum
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {metricas?.tempoMedioEsperaComuns || '0 min'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2.5, bgcolor: alpha('#4caf50', 0.05), borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#2e7d32' }}>
                        Análise Operacional
                      </Typography>
                      
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Serviços com Maior Tempo de Atendimento:</strong>
                      </Typography>
                      
                      <Box sx={{ pl: 2, mb: 2 }}>
                        {servicosPorTempo && servicosPorTempo.length > 0 ? (
                          <>
                            {servicosPorTempo.slice(0, 2).map((servico, index) => (
                              <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                                • {servico.nome}: {servico.tempoMedio || '0 min'}
                              </Typography>
                            ))}
                          </>
                        ) : (
                          <Typography variant="body2">
                            • Dados insuficientes para análise por serviço
                          </Typography>
                        )}
                      </Box>
                      
                      <Typography variant="body2" paragraph sx={{ fontWeight: 500 }}>
                        <strong>Informações de Atendimento:</strong>
                      </Typography>
                      
                      <Box sx={{ pl: 2 }}>
                        {dadosMovimento && dadosMovimento.length > 0 ? (
                          <>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              • Maior fluxo registrado às {
                                (() => {
                                  const horaPico = [...dadosMovimento].sort((a, b) => b.quantidade - a.quantidade)[0];
                                  return horaPico ? horaPico.hora : 'N/D';
                                })()
                              } com {
                                (() => {
                                  const horaPico = [...dadosMovimento].sort((a, b) => b.quantidade - a.quantidade)[0];
                                  return horaPico ? horaPico.quantidade : 0;
                                })()
                              } senhas
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            • Dados insuficientes para análise de pico de atendimento
                          </Typography>
                        )}
                        
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {(() => {
                            // Obter atendimentos preferenciais
                            const preferenciais = dados?.atendimentosPreferenciais || 0;
                            
                            // Calcular total de atendimentos incluindo preferenciais e não preferenciais
                            let totalAtendimentos = 0;
                            
                            // Calcular a soma total de atendimentos a partir dos dados de serviços
                            if (dados?.distribuicaoPorServico && dados.distribuicaoPorServico.length > 0) {
                              totalAtendimentos = dados.distribuicaoPorServico.reduce((total, servico) => 
                                total + servico.quantidade, 0);
                            }
                            
                            // Se por alguma razão não tivermos os dados de serviço, usar o total de senhas emitidas
                            if (totalAtendimentos === 0) {
                              totalAtendimentos = dados?.senhasEmitidas || metricas?.senhasEmitidas || 0;
                            }
                            
                            // Para garantir que totalAtendimentos inclua pelo menos os atendimentos preferenciais
                            totalAtendimentos = Math.max(totalAtendimentos, preferenciais);
                            
                            // Calcular percentual
                            let percentual = 0;
                            if (totalAtendimentos > 0 && preferenciais > 0) {
                              percentual = Math.round((preferenciais / totalAtendimentos) * 100);
                              
                              // Garantir que se houver atendimentos preferenciais, mostramos pelo menos 1%
                              percentual = Math.max(1, percentual);
                            }
                            
                            return (
                              <>
                                • Atendimentos preferenciais: <strong>{preferenciais}</strong> ({percentual}% do total)
                              </>
                            );
                          })()}
                        </Typography>
                        
                        <Typography variant="body2">
                          • Taxa de cancelamento: <strong>{
                            metricas?.senhasEmitidas > 0 && metricas.senhasCanceladas
                              ? ((metricas.senhasCanceladas / metricas.senhasEmitidas) * 100).toFixed(1)
                              : 0
                          }%</strong>
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Novo Card - Análise de Datas */}
                    <Box sx={{ p: 2.5, bgcolor: alpha('#1976d2', 0.05), borderRadius: 2, mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#1565c0' }}>
                        Análise de Datas
                      </Typography>
                      
                      {carregandoDias ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : diasMovimentados.length > 0 ? (
                        <>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Dia mais movimentado:</strong>
                          </Typography>
                          
                          <Box sx={{ pl: 2, mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              • Data: <strong>{diasMovimentados[0].dataFormatada}</strong> ({diasMovimentados[0].totalSenhas} senhas)
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              • Horário de pico: <strong>{diasMovimentados[0].horaPico}</strong> ({diasMovimentados[0].quantidadeNaHoraPico} senhas)
                            </Typography>
                            <Typography variant="body2">
                              • Serviço mais utilizado: <strong>{diasMovimentados[0].servicoMaisUtilizado}</strong> ({diasMovimentados[0].quantidadeDoServicoMaisUtilizado} senhas)
                            </Typography>
                          </Box>
                        </>
                      ) : (
                        <Typography variant="body2">
                          Dados insuficientes para análise de datas no período selecionado.
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
                
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: customTheme.palette.primary.main }}>
                  Recomendações para Gestão
                </Typography>
                
                <Box sx={{ p: 2, bgcolor: alpha('#ff9800', 0.05), borderRadius: 2, border: `1px solid ${alpha('#ff9800', 0.2)}` }}>
                  <Typography variant="body2" paragraph sx={{ mb: 1 }}>
                    <strong>1. Análise de Fluxo:</strong> {
                      dadosMovimento && dadosMovimento.length > 0
                        ? (() => {
                            const horaPico = [...dadosMovimento].sort((a, b) => b.quantidade - a.quantidade)[0];
                            return `Os dados mostram concentração de atendimentos às ${horaPico ? horaPico.hora : 'N/D'} horas com ${horaPico ? horaPico.quantidade : 0} senhas. Recomenda-se avaliação da distribuição de colaboradores neste período.`;
                          })()
                        : 'Os dados disponíveis são insuficientes para uma análise conclusiva de fluxo.'
                    }
                  </Typography>
                  
                  <Typography variant="body2" paragraph sx={{ mb: 1 }}>
                    <strong>2. Análise de Tempos:</strong> {
                      servicosPorTempo && servicosPorTempo.length > 0
                        ? (() => {
                            const servicoMaiorTempo = servicosPorTempo[0];
                            return `O serviço "${servicoMaiorTempo.nome}" apresenta o maior tempo médio de atendimento (${servicoMaiorTempo.tempoMedio}). 
                            Este serviço respondeu por ${servicoMaiorTempo.totalAtendimentos} atendimentos no período analisado.`;
                          })()
                        : `O tempo médio de atendimento registrado foi de ${metricas?.tempoMedioAtendimento || '0 min'} 
                          e o tempo médio de espera de ${tempoMedioEspera}.`
                    }
                  </Typography>
                  
                  <Typography variant="body2" paragraph sx={{ mb: 1 }}>
                    <strong>3. Distribuição por Datas:</strong> {
                      diasMovimentados && diasMovimentados.length > 0
                        ? `A data ${diasMovimentados[0].dataFormatada} foi a mais movimentada com ${diasMovimentados[0].totalSenhas} senhas emitidas, 
                          com pico às ${diasMovimentados[0].horaPico}.`
                        : 'Não há dados suficientes para análise da distribuição por datas.'
                    }
                  </Typography>

                  <Typography variant="body2">
                    <strong>4. Cancelamentos:</strong> {
                      metricas?.senhasCanceladas && metricas.senhasCanceladas > 0
                        ? `Foram registrados ${metricas.senhasCanceladas} cancelamentos no período (${
                            metricas?.senhasEmitidas > 0 
                              ? ((metricas.senhasCanceladas / metricas.senhasEmitidas) * 100).toFixed(1)
                              : 0
                          }% do total).`
                        : 'O volume de cancelamentos está dentro da média esperada para o período analisado.'
                    }
                  </Typography>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default RelatorioGeral; 